import React from 'react';
import { Heart, Brain, Lightbulb, Users } from 'lucide-react';

const values = [
  {
    icon: Heart,
    title: 'Citizen-Centered Design',
    description: 'AI solutions that improve public services while maintaining human oversight and accountability.'
  },
  {
    icon: Brain,
    title: 'Technical Excellence',
    description: 'Combining enterprise AI expertise with deep understanding of public sector compliance and security requirements.'
  },
  {
    icon: Users,
    title: 'Accessible Technology',
    description: 'WCAG 2.1 AAA compliant solutions that work for all citizens, regardless of technical ability or accessibility needs.'
  },
  {
    icon: Lightbulb,
    title: 'Measurable Impact',
    description: 'Transparent ROI tracking and social value reporting aligned with PPN 06/20 and Social Value Act 2012.'
  }
];

export default function FounderStory() {
  return (
    <section className="relative py-12">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/85 to-gray-900/90" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="space-y-8">
          {/* Story Section with Logo */}
          <div className="glass-card p-8 rounded-xl backdrop-blur-md">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-2">
                <h2 className="text-4xl font-bold mb-6">
                  <span className="text-gradient">Our Story</span>
                </h2>
                <div className="prose prose-lg prose-invert max-w-none">
                  <div className="space-y-6 text-lg text-gray-300">
                    <p className="leading-relaxed">
                      At TacticalSolutions.ai, our expertise is built on a foundation of proven business success and technical innovation. Our founder, Anthony Keen, brings over two decades of entrepreneurial experience, having built a multimillion-pound enterprise from the ground up starting at age 17.
                    </p>
                    <p className="leading-relaxed">
                      This journey taught invaluable lessons about operational efficiency, scalability, and the transformative power of automation. During the COVID pandemic, Anthony faced the challenge of maintaining business operations remotely while managing increased family responsibilities—a challenge that accelerated his deep dive into AI automation and digital transformation.
                    </p>
                    <blockquote className="border-l-4 border-teal-500 pl-4 my-6 italic text-gray-200">
                      "The need for efficient, secure, and measurable automation solutions led me to focus on the public sector—where these qualities aren't just nice-to-have, they're mandatory."
                    </blockquote>
                    <p className="leading-relaxed">
                      This personal experience with business automation, combined with extensive study of public sector requirements, frameworks, and compliance standards, positioned TacticalSolutions.ai to become a trusted partner for government organizations seeking reliable AI automation with proven ROI.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Logo Frame */}
              <div className="flex items-center justify-center">
                <img
                  src="https://stackblitz.com/storage/blobs/redirect/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBN0toRmc9PSIsImV4cCI6bnVsbCwicHVyIjoiYmxvYl9pZCJ9fQ==--8ed48ca1e1f8032e164818e9164ed73696e6ab53/Copy%20of%20HS%20Alwafa%20digital.png"
                  alt="TacticalSolutions.ai Logo"
                  className="w-48 h-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="glass-card p-6 rounded-xl backdrop-blur-md transform hover:scale-105 transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-500 to-purple-500 p-2.5 flex items-center justify-center">
                    <value.icon className="w-full h-full text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{value.title}</h3>
                </div>
                <p className="text-gray-300">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}